import { Stack, useTheme } from '@mui/material'
import { Fragment, useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { invalidateCustomer } from 'src/api/customer'
import { axios } from 'src/axios'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { permissionBinding, permissionLevel } from 'src/constant'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import ApiAccessPage from 'src/pages/customer-admin/api-access/ApiAccessPage'
import EquipmentPage from 'src/pages/customer-admin/EquipmentPage'
import ServicesPage from 'src/pages/customer-admin/ServicesPage'
import StoragesPage from 'src/pages/customer-admin/StoragesPage'
import { DocumentCategoriesContainer } from 'src/pages/document-category/CustomerAdminDocumentCategories'
import { DocumentAdminPage } from 'src/pages/locality/DocumentPage'
import { customerQueryKey } from 'src/query-keys'
import AddChecklistTemplate from 'src/screens/customer/checklist-templates/AddChecklistTemplate'
import { ChecklistTemplatesContainer } from 'src/screens/customer/checklist-templates/ChecklistTemplatesContainer'
import EditChecklistTemplate from 'src/screens/customer/checklist-templates/EditChecklistTemplate'
import ComponentOverviewContainer from 'src/screens/customer/component-overview/ComponentOverviewContainer'
import ArchivedDocumentsContainer from 'src/screens/customer/documents/ArchivedDocumentsContainer'
import { ServiceStationGroupsScreen } from 'src/screens/customer/service-station-group/ServiceStationGroupsScreen'
import { ServiceStationScreen } from 'src/screens/customer/service-station/ServiceStationScreen'
import { UserResources } from 'src/screens/customer/users/UserResources'
import { DeviationContainer } from 'src/screens/localities/deviations/DeviationContainer'
import { SelectedMainComponentInCustomerAdmin } from 'src/screens/localities/mainComponents/components/SelectedMainComponentInLocality'
import { AppRoutesNavigation, AppRoutesRouter } from '../../app-routes'
import { CustomerCtx, CustomerCtxProvider } from '../../context/CustomerContext'
import { CustomerFeatureFlagsProvider } from '../../context/CustomerFeatureFlagsProvider'
import { DialogContextType, RoleTypes, SystemPortal } from '../../enums'
import { LoadingGuard } from '../../guards/LoadingGuard'
import { useDialogCtx, useUserCtx } from '../../hooks/context-hooks'
import { getCustomer } from '../../http'
import { CustomPropertyContainer } from '../../screens/admin/custom-type/CustomPropertyContainer'
import { GlobalDocumentCustomerContainer } from '../../screens/admin/global-documents/GlobalDocumentCustomerContainer'
import { CustomerAccessControlScreen } from '../../screens/customer/CustomerAccessControlScreen'
import ChecklistForm from '../../screens/customer/checklists/ChecklistForm'
import ChecklistsPage from '../../screens/customer/checklists/Checklists'
import DeviationsAdminScreen from '../../screens/customer/deviations/DeviationsAdminScreen'
import FormSchemaReportsScreen from '../../screens/customer/form-schema-reports/FormSchemaReportsScreen'
import AddFormSchema from '../../screens/customer/form/AddFormSchema'
import EditFormSchema from '../../screens/customer/form/EditFormSchema'
import FormSchemas from '../../screens/customer/form/FormSchemas'
import { GlobalDocumentsForCustomer } from '../../screens/customer/global-documents/GlobalDocumentsForCustomer'
import InteractiveSchemaForm from '../../screens/customer/interactive-schema/InteractiveSchemaForm'
import InteractiveSchemas from '../../screens/customer/interactive-schema/InteractiveSchemas'
import { LocalitiesScreen } from '../../screens/customer/localities/LocalitiesScreen'
import { LocalityGroupsScreen } from '../../screens/customer/location-groups/LocalityGroupsScreen'
import { MainComponentTypesContainer } from '../../screens/customer/main-component-types/CustomerMainComponentTypesContainer'
import NewsAdminScreen from '../../screens/customer/news/NewsScreen'
import { SupplierContainer } from '../../screens/customer/suppliers/SupplierContainer'
import Tasks from '../../screens/customer/tasks/Tasks'
import AisVesselsPage from 'src/screens/customer/ais-vessels/AisVesselsPage'
import Resources from '../../screens/customer/tasks/resource/Resources'
import TimeReports from '../../screens/customer/tasks/timeReports/TimeReports'
import { UserInvitesScreen } from '../../screens/customer/user-invites/UserInvitesScreen'
import { UsersContainer } from '../../screens/customer/users/UsersContainer'
import { AccessControls } from '@lovoldsystem/authorization.client'
import ReportGeneratorContainer from 'src/screens/customer/report-generator/ReportGeneratorContainer'
import { isDynamicReportsEnabled } from 'src/feature-flags'
import GlobalDocumentLocalityContainer from 'src/screens/admin/global-documents/GlobalDocumentLocalityContainer'

export const CustomerAdminRoutes = () => {
  const { id } = useParams()
  const { data, isLoading } = useQuery(customerQueryKey.GET_CUSTOMER(id), () => getCustomer(id!))
  const [permission, setPermission] = useState(-1)
  const { dialogsCustom, showDialog } = useDialogCtx()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { setActivePortal } = useUserCtx()

  useEffect(() => {
    setActivePortal(SystemPortal.ClientAdmin)
  }, [])

  useEffect(() => {
    if (data) {
      axios.defaults.headers['X-Permissions'] = data.encryptedPermission
      setPermission(data.permission)
      if (data.isGlobalServiceStationProvider && !location.pathname.includes('/manage/users')) {
        navigate(AppRoutesNavigation.Customer.serviceStations(id as EntityId))
      }
    }
  }, [data])

  return (
    <CustomerCtxProvider customer={data!} permission={permission}>
      <CustomerFeatureFlagsProvider customerId={Number(id)}>
        <LoadingGuard loading={isLoading} showSpinner>
          <AuthorizationProvider
            permission={permission}
            permissions={[
              permissionValue.ClientAdminPermission,
              permissionValue.ClientAdminPortal.Task_Portal,
              permissionValue.ClientAdminPortal.Equipment_Portal,
            ]}
            self_auth={true}
            type={AuthorizationType.Page}
          >
            <Fragment>
              {Object.values(dialogsCustom[DialogContextType.customerAdminContext] ?? {})}
              <Stack
                style={{ backgroundColor: theme.palette.backgroundMain[theme.palette.mode] }}
                width={'100%'}
                minHeight={'calc(100vh - 180px)'}
                className="pb-4-core"
              >
                <Routes>
                  <Route path={AppRoutesRouter.Customer.serviceStations} element={<ServiceStationScreen />} />
                  <Route
                    path={AppRoutesRouter.Customer.users}
                    element={<UsersContainer isGlobalServiceProvider={data?.isGlobalServiceStationProvider} />}
                  />
                  <Route path={AppRoutesRouter.Customer.userResources} element={<UserResources />} />
                  {!data?.isGlobalServiceStationProvider == true && (
                    <>
                      <Route path={AppRoutesRouter.Customer.accessControl} element={<CustomerAccessControlScreen />} />
                      <Route
                        path={AppRoutesRouter.Customer.globalDocumentsAdmin}
                        element={<GlobalDocumentsForCustomer />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.globalDocumentsCustomer}
                        element={<GlobalDocumentCustomerContainer />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.localityGlobalDocuments}
                        element={<GlobalDocumentLocalityContainer />}
                      />

                      <Route
                        path={AppRoutesRouter.Customer.accessControls}
                        element={
                          <AccessControls
                            permissionLevels={permissionLevel}
                            permissionBinding={permissionBinding}
                            clientId={parseInt(id || '')}
                            roleTypes={RoleTypes}
                            showDialog={showDialog}
                            theme={theme}
                            height={window.innerHeight - 250 + 'px'}
                            handleInvalidations={() => {
                              invalidateCustomer.getUserRoles(parseInt(id || ''))
                            }}
                          />
                        }
                      />
                      <Route path={AppRoutesRouter.Customer.localities} element={<LocalitiesScreen />} />
                      <Route path={AppRoutesRouter.Customer.localityGroup} element={<LocalityGroupsScreen />} />
                      <Route path={AppRoutesRouter.Customer.storages} element={<StoragesPage />} />
                      <Route
                        path={AppRoutesRouter.Customer.serviceStationGroup}
                        element={<ServiceStationGroupsScreen />}
                      />
                      <Route path={AppRoutesRouter.Customer.workOrders} element={<Tasks />} />
                      <Route path={AppRoutesRouter.Customer.vessels} element={<Resources />} />
                      <Route path={AppRoutesRouter.Customer.aisVessels} element={<AisVesselsPage />} />
                      <Route path={AppRoutesRouter.Customer.timeReports} element={<TimeReports />} />
                      <Route path={AppRoutesRouter.Customer.userInvites} element={<UserInvitesScreen />} />
                      <Route path={AppRoutesRouter.Customer.customProperty} element={<CustomPropertyContainer />} />
                      <Route path={AppRoutesRouter.Customer.apiAccess} element={<ApiAccessPage />} />
                      <Route
                        path={AppRoutesRouter.Customer.documentCategories}
                        element={<DocumentCategoriesContainer />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.checklistTemplates}
                        element={<ChecklistTemplatesContainer />}
                      />
                      <Route path={AppRoutesRouter.Customer.addChecklistTemplate} element={<AddChecklistTemplate />} />
                      <Route
                        path={AppRoutesRouter.Customer.editChecklistTemplate}
                        element={<EditChecklistTemplate />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.mainComponentTypes}
                        element={<MainComponentTypesContainer />}
                      />
                      <Route path={AppRoutesRouter.Customer.formSchemas} element={<FormSchemas />} />
                      <Route path={AppRoutesRouter.Customer.formSchemaReports} element={<FormSchemaReportsScreen />} />
                      <Route path={AppRoutesRouter.Customer.formGenerator} element={<AddFormSchema />} />
                      <Route path={AppRoutesRouter.Customer.editFormSchema} element={<EditFormSchema />} />
                      <Route path={AppRoutesRouter.Customer.interactiveSchemas} element={<InteractiveSchemas />} />
                      <Route
                        path={AppRoutesRouter.Customer.createInteractiveSchema}
                        element={<InteractiveSchemaForm />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.editInteractiveSchema}
                        element={<InteractiveSchemaForm />}
                      />
                      <Route path={AppRoutesRouter.Customer.checklists} element={<ChecklistsPage />} />
                      <Route path={AppRoutesRouter.Customer.createChecklist} element={<ChecklistForm />} />
                      <Route path={AppRoutesRouter.Customer.editChecklist} element={<ChecklistForm />} />
                      {isDynamicReportsEnabled && (
                        <Route path={AppRoutesRouter.Customer.dynamicReports} element={<ReportGeneratorContainer />} />
                      )}
                      <Route path={AppRoutesRouter.Customer.deviationsAdmin} element={<DeviationsAdminScreen />} />
                      <Route path={AppRoutesRouter.Customer.newsAdmin} element={<NewsAdminScreen />} />
                      <Route path={AppRoutesRouter.Customer.services} element={<ServicesPage />} />
                      <Route path={AppRoutesRouter.Customer.mainComponents} element={<EquipmentPage />} />
                      <Route
                        path={AppRoutesRouter.Customer.mainComponent}
                        element={<SelectedMainComponentInCustomerAdmin />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.deviation}
                        element={<DeviationContainer context={CustomerCtx} />}
                      />
                      <Route path={AppRoutesRouter.Customer.suppliers} element={<SupplierContainer />} />
                      <Route path={AppRoutesRouter.Customer.document} element={<DocumentAdminPage />} />
                      <Route path="*" element={<Navigate to={AppRoutesRouter.Customer.accessControl} />} />
                      <Route
                        path={AppRoutesRouter.Customer.archivedDocuments}
                        element={<ArchivedDocumentsContainer />}
                      />
                      <Route
                        path={AppRoutesRouter.Customer.componentOverview}
                        element={<ComponentOverviewContainer />}
                      />
                    </>
                  )}
                </Routes>
              </Stack>
            </Fragment>
          </AuthorizationProvider>
        </LoadingGuard>
      </CustomerFeatureFlagsProvider>
    </CustomerCtxProvider>
  )
}
